import * as React from 'react';

import { Collapse, color } from '@coursera/coursera-ui';
import { SvgChevronRight } from '@coursera/coursera-ui/svg';

import { withMediaMaxWidthScreenTablet } from 'bundles/browse/utils';
import CareerPlansCourseCard from 'bundles/career-plans/components/career-card/CareerPlansCourseCard';
import CareerPlansCourseCardMobile from 'bundles/career-plans/components/career-card/CareerPlansCourseCardMobile';
import type { OrderedCareerType } from 'bundles/career-plans/components/data/types/career-plans';
import { CAREER_NAME_TO_SLUG, DATA_SCIENTIST_SENIOR } from 'bundles/career-plans/constants';
import TrackedDiv from 'bundles/page/components/TrackedDiv';

import _t from 'i18n!nls/career-plans';

import 'css!./__styles__/CareerCard';

type PropsFromHoc = { matches: boolean };
type PropsFromInput = OrderedCareerType;
type Props = PropsFromInput & PropsFromHoc;

type CareerCardHeaderProps = OrderedCareerType & {
  computedLevel: string;
  isMobile?: boolean;
  isOpen?: boolean;
};

export const CareerCardHeader = ({
  name,
  keySkills = [],
  description = '',
  computedLevel,
  isMobile,
  isOpen,
}: CareerCardHeaderProps) => {
  return (
    <section className="careerCard-card">
      <TrackedDiv
        className="careerCard-container is-top careerCard-padding careerCard-card--header"
        data-e2e="CareerPlansCareerCard-header"
        trackingName="careerplans_career_card"
        withVisibilityTracking={true}
        requireFullyVisible={false}
      >
        <div className="careerCard-container--left">
          <h2 className="careerCard-header" data-e2e="CareerPlansCareerCard-header--name">
            {name}
          </h2>
          <h2 className="careerPlans-sectionHeader careerCard-level" data-e2e="CareerPlansCareerCard-header--level">
            {computedLevel}
          </h2>
        </div>
        {!isMobile && (
          <div className="careerCard-container--right careerCard-container--picturePadding careerPlans-text">
            {description.length > 0 && (
              <div className="careerCard-container--right_text" data-e2e="CareerPlansCareerCard-header--description">
                {description}
              </div>
            )}
          </div>
        )}
        {keySkills.length > 0 && (
          <div className="careerCard-keySkills" data-e2e="CareerPlansCareerCard-header--keySkills">
            <h3 className="careerCard-keySkills--header">{_t('Key Skills to Learn')}</h3>
            <div>{keySkills.join(', ')}</div>
          </div>
        )}
        {isMobile && (
          <div className="careerCard-mobileCollapse">
            <div className="mobileCollapse-button" data-e2e="CareerPlansCareerCard-header--mobile-button">
              <div>
                <div className="mobileCollapse-button-label">{isOpen ? _t('Hide details') : _t('See details')}</div>
                <SvgChevronRight
                  style={{ transform: isOpen ? 'rotate(-90deg)' : 'rotate(90deg)', borderRadius: 2, margin: 2 }}
                  color={color.primary}
                  aria-hidden={true}
                />
              </div>
            </div>
            {isOpen && (
              <div className="careerCard-container--right careerPlans-text">
                {description.length > 0 && (
                  <div data-e2e="CareerPlansCareerCard-header--mobile-description">{description}</div>
                )}
              </div>
            )}
          </div>
        )}
      </TrackedDiv>
    </section>
  );
};

export const CareerCardCourses = ({
  name,
  foundationalCourses = [],
  showcasedCourses = [],
  isMobile,
  isOpen,
  computedLevel,
  careerPosition,
}: CareerCardHeaderProps) => {
  const CourseCardComponent = isMobile ? CareerPlansCourseCardMobile : CareerPlansCourseCard;
  return (
    <section className="careerCard-card--courses" data-e2e="CareerPlansCareerCard-body--courses">
      <div className="mobile-spacer" />
      {(!isMobile || isOpen) && (
        <div>
          {foundationalCourses.length > 0 &&
            foundationalCourses.map((courseInfo, positionIndex) => (
              <CourseCardComponent
                data-e2e="CareerPlansProductCard-foundationalCard"
                key={`foundationalCourse-${courseInfo.name}`}
                isFoundational={true}
                {...courseInfo}
                careerName={name}
                positionIndex={positionIndex + 1}
                careerPosition={careerPosition}
                computedLevel={computedLevel}
              />
            ))}
          {showcasedCourses.length > 0 &&
            showcasedCourses.map((courseInfo, positionIndex) => (
              <CourseCardComponent
                data-e2e="CareerPlansProductCard-showcasedCard"
                key={`showcasedCourse-${courseInfo.name}`}
                {...courseInfo}
                careerName={name}
                positionIndex={positionIndex + 1}
                careerPosition={careerPosition}
                computedLevel={computedLevel}
              />
            ))}
        </div>
      )}
    </section>
  );
};

export const CareerCard = (props: Props) => {
  const { name, level, skillLevel, matches } = props;
  const computedLevel = skillLevel || level;
  let anchor_link_id;
  if (!name || !computedLevel) return null;
  if (name === DATA_SCIENTIST_SENIOR.DATA_SCIENTIST && skillLevel === DATA_SCIENTIST_SENIOR.SENIOR_LEVEL) {
    anchor_link_id = `${CAREER_NAME_TO_SLUG[name]}~${skillLevel}`;
  } else {
    anchor_link_id = `${CAREER_NAME_TO_SLUG[name]}`;
  }

  return (
    <section className="rc-CareerCard">
      {matches && (
        <Collapse>
          <Collapse.Panel
            renderHeader={({ isExpanded }) => (
              <CareerCardHeader {...props} computedLevel={computedLevel} isMobile={true} isOpen={isExpanded} />
            )}
            renderChildren={({ isExpanded }) => (
              <CareerCardCourses {...props} computedLevel={computedLevel} isMobile={true} isOpen={isExpanded} />
            )}
          />
        </Collapse>
      )}
      {!matches && (
        <div>
          <div className="career-card-anchor-link-id" id={anchor_link_id} />
          <CareerCardHeader {...props} computedLevel={computedLevel} />
          <CareerCardCourses {...props} computedLevel={computedLevel} />
        </div>
      )}
    </section>
  );
};

export default withMediaMaxWidthScreenTablet(CareerCard);
